/**
 * Service to handle communication with the Convai gRPC-Web API
 * Based directly on the example_grpc_client.ts implementation
 */
import { grpc } from '@improbable-eng/grpc-web';
import { ConvaiService, proto } from './grpcHelper';

// const GRPC_HOST = "https://webstream.convai.com";
// const SOURCE_TAG = "Web";

const GRPC_HOST = "https://webstream.convai.com";
const SOURCE_TAG = "SL-Web-App";

// Create metadata for all requests
const METADATA = new grpc.Metadata();
METADATA.set('source', SOURCE_TAG);

class ConvaiClient {
  constructor(apiKey, characterId) {
    this.apiKey = apiKey;
    this.characterId = characterId;
    this.sessionId = "-1"; // Initial session ID
    this.languageCode = "en-US";
    this.isStarted = false;
    this.finishedSending = false;
    this.enableAudio = false; // Disable audio by default
    this.audioSampleRate = 44100;
    this.messageCallback = null;
    this.errorCallback = null;
    this.inputMode = "text";
    this.interactionId = null;
    
    // For combining streaming messages into one
    this.currentResponseBuffer = "";
    this.responseTimeoutId = null;
    this.responseEndTimer = 500; // Wait 500ms after receiving a chunk to send the combined message
    this.isResponding = false;  // Add this flag to track response state
    
    // Initialize the clients
    this.client = grpc.client(ConvaiService.GetResponse, {
      host: GRPC_HOST,
      transport: grpc.WebsocketTransport()
    });
    
    this.feedbackClient = grpc.client(ConvaiService.SubmitFeedback, {
      host: GRPC_HOST,
      transport: grpc.WebsocketTransport()
    });
    
    // Set up response handler
    this.client.onMessage((response) => {
      if (this.messageCallback) {
        try {
          let textChunk = null;
          let receivedInteractionId = null;
          
          // First check for interaction ID
          if (typeof response.getInteractionId === 'function') {
            receivedInteractionId = response.getInteractionId();
          } else if (response.interactionId) {
            receivedInteractionId = response.interactionId;
          } else if (response.interaction_id) {
            receivedInteractionId = response.interaction_id;
          }

          // Update stored interaction ID if we received one
          if (receivedInteractionId) {
            console.log("Received interaction ID:", receivedInteractionId);
            this.interactionId = receivedInteractionId;
          }
          
          // Check for audio response with text
          if (typeof response.getAudioResponse === 'function') {
            const audioResponse = response.getAudioResponse();
            if (audioResponse && typeof audioResponse.getTextData === 'function') {
              textChunk = audioResponse.getTextData();
            }
          }
          
          // Try alternative ways to extract text
          if (!textChunk && response.audioResponse && response.audioResponse.textData) {
            textChunk = response.audioResponse.textData;
          } else if (!textChunk && response.audio_response && response.audio_response.text_data) {
            textChunk = response.audio_response.text_data;
          }
          
          // If we have an interaction ID but no text, send an empty message
          if (this.interactionId && !textChunk) {
            textChunk = '';
          }
          
          // If we found text or have an interaction ID, process it
          if (textChunk !== null) {
            if (!this.isResponding) {
              this.isResponding = true;
            }
            
            // Add to the buffer
            this.currentResponseBuffer += textChunk;
            
            // Clear any existing timeout
            if (this.responseTimeoutId) {
              clearTimeout(this.responseTimeoutId);
            }
            
            // Set new timeout to send the complete message
            this.responseTimeoutId = setTimeout(() => {
              if (this.currentResponseBuffer !== null) {
                this.messageCallback(this.currentResponseBuffer);
                this.currentResponseBuffer = ""; // Clear buffer after sending
                this.isResponding = false;
              }
            }, this.responseEndTimer);
          }
          
          // Update session ID if available
          if (typeof response.getSessionId === 'function') {
            const sessionId = response.getSessionId();
            if (sessionId) {
              this.sessionId = sessionId;
            }
          } else if (response.sessionId) {
            this.sessionId = response.sessionId;
          } else if (response.session_id) {
            this.sessionId = response.session_id;
          }
        } catch (error) {
          console.error("Error handling message:", error);
        }
      }
    });
    
    // Set up error handler
    this.client.onEnd((status, statusMessage, trailers) => {
      if (status !== grpc.Code.OK) {
        console.log('GetResponse Failed: ', status, statusMessage);
        if (this.errorCallback) {
          this.errorCallback("chatbotResponseError", statusMessage, String(status));
        }
      }
      
      // Reset connection state
      this.isStarted = false;
    });
    
    // Set up feedback response handler
    this.feedbackClient.onMessage((response) => {
      console.log("Received feedback response:", response);
    });
    
    // Set up feedback error handler
    this.feedbackClient.onEnd((status, statusMessage, trailers) => {
      if (status !== grpc.Code.OK) {
        console.log('SubmitFeedback Failed: ', status, statusMessage);
        if (this.errorCallback) {
          this.errorCallback("feedbackResponseError", statusMessage, String(status));
        }
      }
    });
  }

  /**
   * Sets a callback to receive messages from the Convai service
   * @param {Function} callback - Function to call when a message is received
   */
  onMessage(callback) {
    this.messageCallback = callback;
  }

  /**
   * Sets a callback to receive error messages
   * @param {Function} callback - Function to call when an error occurs
   */
  onError(callback) {
    this.errorCallback = callback;
  }

  /**
   * Starts the connection to the Convai service
   * @returns {boolean} Whether the connection was started successfully
   */
  start() {
    // If client has finished sending, we need to recreate it
    if (this.finishedSending) {
      this.recreateClient();
    }

    if (!this.isStarted) {
      try {
        this.client.start(METADATA);
        this.sendFirstRequest();
        return true;
      } catch (error) {
        console.error("Error starting client:", error);
        if (error.message && error.message.includes("Client already started")) {
          this.isStarted = true;
          return true;
        } else {
          if (this.errorCallback) {
            this.errorCallback("connectionError", error.message, "START_ERROR");
          }
          return false;
        }
      }
    }
    return true;
  }
  
  /**
   * Recreates the gRPC client
   */
  recreateClient() {
    // Clear any existing response timeout
    if (this.responseTimeoutId) {
      clearTimeout(this.responseTimeoutId);
      this.responseTimeoutId = null;
    }
    
    // Reset the buffer
    this.currentResponseBuffer = "";
    
    // Close existing client if it exists
    if (this.client) {
      try {
        this.client.close();
      } catch (error) {
        console.log("Error closing existing client:", error);
      }
    }
    
    // Create a new client instance
    this.client = grpc.client(ConvaiService.GetResponse, {
      host: GRPC_HOST,
      transport: grpc.WebsocketTransport()
    });
    
    // Set up response handler
    this.client.onMessage((response) => {
      if (this.messageCallback) {
        try {
          let textChunk = null;
          let receivedInteractionId = null;
          
          // First check for interaction ID
          if (typeof response.getInteractionId === 'function') {
            receivedInteractionId = response.getInteractionId();
          } else if (response.interactionId) {
            receivedInteractionId = response.interactionId;
          } else if (response.interaction_id) {
            receivedInteractionId = response.interaction_id;
          }

          // Update stored interaction ID if we received one
          if (receivedInteractionId) {
            console.log("Received interaction ID:", receivedInteractionId);
            this.interactionId = receivedInteractionId;
          }
          
          // Check for audio response with text
          if (typeof response.getAudioResponse === 'function') {
            const audioResponse = response.getAudioResponse();
            if (audioResponse && typeof audioResponse.getTextData === 'function') {
              textChunk = audioResponse.getTextData();
            }
          }
          
          // Try alternative ways to extract text
          if (!textChunk && response.audioResponse && response.audioResponse.textData) {
            textChunk = response.audioResponse.textData;
          } else if (!textChunk && response.audio_response && response.audio_response.text_data) {
            textChunk = response.audio_response.text_data;
          }
          
          // If we have an interaction ID but no text, send an empty message
          if (this.interactionId && !textChunk) {
            textChunk = '';
          }
          
          // If we found text or have an interaction ID, process it
          if (textChunk !== null) {
            if (!this.isResponding) {
              this.isResponding = true;
            }
            
            // Add to the buffer
            this.currentResponseBuffer += textChunk;
            
            // Clear any existing timeout
            if (this.responseTimeoutId) {
              clearTimeout(this.responseTimeoutId);
            }
            
            // Set new timeout to send the complete message
            this.responseTimeoutId = setTimeout(() => {
              if (this.currentResponseBuffer !== null) {
                this.messageCallback(this.currentResponseBuffer);
                this.currentResponseBuffer = ""; // Clear buffer after sending
                this.isResponding = false;
              }
            }, this.responseEndTimer);
          }
          
          // Update session ID if available
          if (typeof response.getSessionId === 'function') {
            const sessionId = response.getSessionId();
            if (sessionId) {
              this.sessionId = sessionId;
            }
          } else if (response.sessionId) {
            this.sessionId = response.sessionId;
          } else if (response.session_id) {
            this.sessionId = response.session_id;
          }
        } catch (error) {
          console.error("Error handling message:", error);
        }
      }
    });
    
    // Set up error handler
    this.client.onEnd((status, statusMessage, trailers) => {
      if (status !== grpc.Code.OK) {
        console.log('GetResponse Failed: ', status, statusMessage);
        if (this.errorCallback) {
          this.errorCallback("chatbotResponseError", statusMessage, String(status));
        }
      }
      
      // Reset connection state
      this.isStarted = false;
    });
    
    // Reset state
    this.isStarted = false;
    this.finishedSending = false;
  }

  /**
   * Sends the initial configuration request
   */
  sendFirstRequest() {
    try {
      // Create config request exactly as in the example
      const firstReq = new proto.GetResponseRequest();
      const getResponseConfig = new proto.GetResponseRequest.GetResponseConfig();
      
      getResponseConfig.setApiAuthToken(this.apiKey);
      // getResponseConfig.setApiKey(this.apiKey);
      getResponseConfig.setCharacterId(this.characterId);
      getResponseConfig.setSessionId(this.sessionId);
      getResponseConfig.setLanguageCode(this.languageCode);
      
      const audioConfig = new proto.AudioConfig();
      audioConfig.setSampleRateHertz(this.audioSampleRate);
      audioConfig.setDisableAudio(!this.enableAudio);
      getResponseConfig.setAudioConfig(audioConfig);
      
      const actionConfig = new proto.ActionConfig();
      getResponseConfig.setActionConfig(actionConfig);
      
      firstReq.setGetResponseConfig(getResponseConfig);
      
      this.client.send(firstReq);
      this.isStarted = true;
    } catch (error) {
      console.error("Error sending first request:", error);
      if (this.errorCallback) {
        this.errorCallback("configError", error.message, "CONFIG_ERROR");
      }
    }
  }

  /**
   * Sends a text message to the Convai service
   * @param {string} text - The message text to send
   * @returns {boolean} Whether the message was sent successfully
   */
  sendText(text) {
    if (this.inputMode === 'audio') {
      console.log('Error: Cannot send text in audio input mode');
      return false;
    }
    
    this.inputMode = 'text';
    
    // If client has finished sending, we need to recreate it
    if (this.finishedSending) {
      this.recreateClient();
    }
    
    if (!this.isStarted) {
      if (!this.start()) {
        return false;
      }
    }
    
    if (text && text.length > 0) {
      try {
        const req = new proto.GetResponseRequest();
        const getResponseData = new proto.GetResponseRequest.GetResponseData();
        getResponseData.setTextData(text);
        req.setGetResponseData(getResponseData);
        
        this.client.send(req);
        this.client.finishSend();
        this.finishedSending = true;
        
        return true;
      } catch (error) {
        console.error("Error sending text:", error);
        if (this.errorCallback) {
          this.errorCallback("sendError", error.message, "SEND_ERROR");
        }
        return false;
      }
    }
    
    return false;
  }

  /**
   * Sends a message to the service (legacy compatibility method)
   * @param {string} text - The message to send
   * @returns {Promise} - Promise that resolves when the message is sent
   */
  async sendMessage(text) {
    return new Promise((resolve, reject) => {
      try {
        if (!this.apiKey || !this.characterId) {
          throw new Error('API key and character ID are required. Please check settings.');
        }
        
        const success = this.sendText(text);
        if (success) {
          resolve(true);
        } else {
          reject(new Error('Failed to send message'));
        }
      } catch (error) {
        console.error('Error sending message:', error);
        reject(error);
      }
    });
  }

  /**
   * Closes the connection to the Convai service
   */
  close() {
    if (this.isStarted) {
      this.isStarted = false;
      
      // Clear any pending response timeout
      if (this.responseTimeoutId) {
        clearTimeout(this.responseTimeoutId);
        this.responseTimeoutId = null;
      }
      
      // Send any buffered text before closing
      if (this.currentResponseBuffer && this.currentResponseBuffer.length > 0 && this.messageCallback) {
        this.messageCallback(this.currentResponseBuffer);
        this.currentResponseBuffer = "";
      }
      
      try {
        this.client.close();
        // Reset the finishedSending flag when closing
        this.finishedSending = false;
      } catch (error) {
        console.error("Error closing client:", error);
      }
    }
  }

  /**
   * Disconnects from the service (legacy compatibility method)
   */
  disconnect() {
    this.close();
    // Create a new client instance for future use
    this.recreateClient();
  }
  
  /**
   * Returns the connection status
   */
  get isConnected() {
    return this.isStarted;
  }
  
  /**
   * Send feedback to the Convai service
   * @param {boolean} thumbsUp - Whether the feedback is positive or negative
   * @param {string} feedbackText - Feedback text (optional)
   * @param {string} interactionId - Interaction ID (optional)
   * @returns {boolean} - Whether the feedback was sent successfully
   */
  sendFeedback(thumbsUp, feedbackText = '', interactionId = null) {
    // Use passed interactionId or fallback to stored one
    const feedbackInteractionId = interactionId || this.interactionId;
    
    if (!feedbackInteractionId) {
      console.error("No interaction ID available for feedback");
      return false;
    }
    
    try {
      console.log("Sending feedback:", {
        thumbsUp: thumbsUp ? "👍" : "👎",
        feedbackText,
        interactionId: feedbackInteractionId
      });
      
      // Create a new feedback client instance for each feedback submission
      this.feedbackClient = grpc.client(ConvaiService.SubmitFeedback, {
        host: GRPC_HOST,
        transport: grpc.WebsocketTransport()
      });
      
      // Set up feedback response handler
      this.feedbackClient.onMessage((response) => {
        console.log("Received feedback response:", response);
      });
      
      // Set up feedback error handler
      this.feedbackClient.onEnd((status, statusMessage, trailers) => {
        if (status !== grpc.Code.OK) {
          console.log('SubmitFeedback Failed: ', status, statusMessage);
          if (this.errorCallback) {
            this.errorCallback("feedbackResponseError", statusMessage, String(status));
          }
        }
      });
      
      // Start the feedback client
      this.feedbackClient.start(METADATA);
      
      // Create feedback request
      const req = new proto.FeedbackRequest();
      const feedback = new proto.FeedbackRequest.Feedback();
      
      feedback.setThumbsUp(thumbsUp);
      feedback.setFeedbackText(feedbackText);
      
      req.setTextFeedback(feedback);
      req.setCharacterId(this.characterId || "");
      req.setInteractionId(feedbackInteractionId);
      req.setSessionId(this.sessionId || "");
      
      // Send the feedback
      this.feedbackClient.send(req);
      this.feedbackClient.finishSend();
      
      return true;
    } catch (error) {
      console.error("Error sending feedback:", error);
      if (this.errorCallback) {
        this.errorCallback("feedbackError", error.message, "FEEDBACK_ERROR");
      }
      return false;
    }
  }
}

export default ConvaiClient;
